import React, {ReactElement, ReactNode} from "react";
import {LabeledSection, LabeledValue} from "../../../components/Layout/Page";
import {Device, DeviceDriverCardField, DeviceField} from "../../../app/api/model/device";
import {useTranslation} from "react-i18next";
import {Chip} from "@mui/material";
import {AddLink as AddLinkIcon, LinkOff as RemoveLinkIcon} from "@mui/icons-material";
import {useModal} from "../../../hooks/useModal";
import DeviceAssignToOrganizationDialog from "../components/DeviceAssignToOrganizationDialog";
import DeviceRemoveFromOrganizationDialog from "../components/DeviceRemoveFromOrganizationDialog";
import {FirmwareField} from "../../../app/api/model/firmware";
import {convertDeviceToDeviceFlat} from "../../../app/api/model/device/helper";
import {EMPTY_UI} from "../../../variables";
import {OrganizationField} from "../../../app/api/model/organization";
import DeviceCapabilities from "../components/DeviceCapabilities";
import PLSentSection from "../components/PLSentSection";
import {PermissionLevel} from "../../../app/api/model/user";

interface DeviceGeneralTabProps {
    device: Device;
}

const DeviceGeneralTab = ({device}: DeviceGeneralTabProps) => {
    const {t} = useTranslation();
    const assignDeviceToOrgModal = useModal(false);
    const removeDeviceToOrgModal = useModal(false);

    const getLabeledValue = (
        labelKey: string,
        content: number | string | ReactElement | ReactNode | null,
        showCopy = false
    ) => <LabeledValue label={t(labelKey)} content={content} key={labelKey} isCopyAlwaysVisible={showCopy} />;
    const fields = [
        {
            label: "generic_id",
            content: device[DeviceField.ID],
        },
        {
            label: "generic_iccid",
            content: device[DeviceField.ICCID],
        },
        {
            label: "device_imei",
            content: device[DeviceField.IMEI],
        },
        {
            label: "device_msisdn",
            content: device[DeviceField.MSISDN],
        },
        {
            label: "device_imsi",
            content: device[DeviceField.IMSI],
        },
        {
            label: "device_serialNumber",
            content: device[DeviceField.SERIAL_NUMBER],
        },
        {
            label: "generic_status",
            content: device[DeviceField.ONLINE] ? (
                <Chip label="Online" color="success" size="small" />
            ) : (
                <Chip label="Offline" color="error" size="small" />
            ),
        },
        {
            label: "device_lastConnectionAt",
            content: device[DeviceField.LAST_CONNECTION_AT],
        },
        {
            label: "device_currentFirmwareVersion",
            content: device[DeviceField.CURRENT_FIRMWARE]?.[FirmwareField.VERSION_NUMBER] || EMPTY_UI,
        },
        {
            label: "device_targetFirmwareVersion",
            content: device[DeviceField.TARGET_FIRMWARE]?.[FirmwareField.VERSION_NUMBER] || EMPTY_UI,
        },
        {
            label: "device_hardwareType",
            content: device[DeviceField.HARDWARE_TYPE],
        },
        {
            label: "device_modemFw",
            content: device?.[DeviceField.SYSTEM_INFORMATION]?.lpwaFirmwareVersion,
        },
        {
            label: "generic_leoId",
            content: device[DeviceField.LEO_ID],
        },
        {
            label: "generic_predecessorLeoId",
            content: device[DeviceField.PREDECESSOR_LEO_ID],
        },
    ];
    const organizationFields = [
        {
            label: "generic_id",
            content: device[DeviceField.ORGANIZATION]?.[OrganizationField.ID],
        },
        {
            label: "organizations_name",
            content: device[DeviceField.ORGANIZATION]?.[OrganizationField.NAME],
        },
        {
            label: "organizations_mainDKVCustomerNumber",
            content: device[DeviceField.ORGANIZATION]?.[OrganizationField.MAIN_DKV_CUSTOMER_NUMBER],
        },
    ];

    const fmsString = device?.[DeviceField.SYSTEM_INFORMATION]?.fmsVersion;
    const fmsVersions = fmsString?.split("/");
    const displayedFmsString =
        fmsVersions?.length === 2
            ? `${fmsVersions[0]} (${t("device_fms_truck")}) / ${fmsVersions[1]} (${t("device_fms_bus")}) `
            : fmsString;
    const connectionType = device?.[DeviceField.SYSTEM_INFORMATION]?.connectionType.length
        ? t(`device_connection_type_${device?.[DeviceField.SYSTEM_INFORMATION]?.connectionType}`)
        : "";
    const systemInformationFields = [
        {
            label: "device_connection_type",
            content: connectionType,
        },
        {
            label: "device_fms_version",
            content: displayedFmsString,
        },
        {
            label: "device_can_click_module",
            content: device?.[DeviceField.SYSTEM_INFORMATION]?.canClickModule,
        },
        {
            label: "device_can_click_firmware",
            content: device?.[DeviceField.SYSTEM_INFORMATION]?.canClickFirmware,
        },
        {
            label: "tacho_brand",
            content: device?.[DeviceField.TACHO_INFORMATION]?.brand,
        },
        {
            label: "tacho_hwPartNo",
            content: device?.[DeviceField.TACHO_INFORMATION]?.hardwarePartNumber,
        },
        {
            label: "tacho_hwVersion",
            content: device?.[DeviceField.TACHO_INFORMATION]?.hardwareVersion,
        },
        {
            label: "tacho_swPartNo",
            content: device?.[DeviceField.TACHO_INFORMATION]?.softwarePartNumber,
        },
        {
            label: "tacho_swVersion",
            content: device?.[DeviceField.TACHO_INFORMATION]?.softwareVersion,
        },
    ];
    const tachoStatusFields = [
        {
            label: "tacho_authenticationStatus",
            content: t(
                device?.[DeviceField.TACHO_INFORMATION]?.authenticationStatus
                    ? "tacho_authenticatedYes"
                    : "tacho_authenticatedNo"
            ),
        },
        {
            label: "tacho_licensePlate",
            content: device?.[DeviceField.TACHO_INFORMATION]?.licensePlate,
        },
        {
            label: "tacho_registeringMemberState",
            content: device?.[DeviceField.TACHO_INFORMATION]?.registeringMemberState,
        },
        {
            label: "tacho_vin",
            content: device?.[DeviceField.TACHO_INFORMATION]?.vin,
        },
        {
            label: "tacho_driverCardSlot1",
            content:
                device?.[DeviceField.DRIVERCARDS]?.find((card) => card.slot === 1)?.[
                    DeviceDriverCardField.DRIVERCARD_ID
                ] || (device[DeviceField.SLOT1_DRIVER_CARD_PRESENT] ? t("device_tachoCardIdUnavailable") : "-"),
        },
        {
            label: "tacho_driverCardSlot2",
            content:
                device?.[DeviceField.DRIVERCARDS]?.find((card) => card.slot === 2)?.[
                    DeviceDriverCardField.DRIVERCARD_ID
                ] || (device[DeviceField.SLOT2_DRIVER_CARD_PRESENT] ? t("device_tachoCardIdUnavailable") : "-"),
        },
    ];
    return (
        <>
            <LabeledSection title={t("generic_device")}>
                {fields.map((f) => getLabeledValue(f.label, f.content))}
            </LabeledSection>
            <LabeledSection
                title={t("generic_organization")}
                buttons={
                    device[DeviceField.ORGANIZATION]?.[OrganizationField.ID]
                        ? [
                              {
                                  key: "remove",
                                  tooltip: "action_removeDeviceFromOrg",
                                  disabled: removeDeviceToOrgModal.isOpen,
                                  minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                  callback: () => removeDeviceToOrgModal.setIsOpen(true),
                                  icon: <RemoveLinkIcon />,
                              },
                          ]
                        : [
                              {
                                  key: "assign",
                                  tooltip: "action_assignDeviceToOrg",
                                  disabled: assignDeviceToOrgModal.isOpen,
                                  minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                  callback: () => assignDeviceToOrgModal.setIsOpen(true),
                                  icon: <AddLinkIcon />,
                              },
                          ]
                }
            >
                {organizationFields.map((f) => getLabeledValue(f.label, f.content))}
            </LabeledSection>

            <LabeledSection title={t("device_capabilities")}>
                <DeviceCapabilities
                    deviceModel={device[DeviceField.HARDWARE_TYPE]}
                    capabilities={device[DeviceField.CAPABILITIES]}
                />
            </LabeledSection>
            {device && (
                <PLSentSection
                    plSentId={device[DeviceField.PL_SENT_ID]}
                    plSentStatus={device[DeviceField.PL_SENT_STATUS]}
                    leoId={device[DeviceField.LEO_ID]}
                />
            )}
            {device && (
                <DeviceAssignToOrganizationDialog
                    devices={[convertDeviceToDeviceFlat(device)]}
                    open={assignDeviceToOrgModal.isOpen}
                    onClose={assignDeviceToOrgModal.toggleIsOpen}
                />
            )}
            <LabeledSection title={t("device_systemInformation")}>
                {systemInformationFields.map((f) => getLabeledValue(f.label, f.content))}
            </LabeledSection>
            <LabeledSection title={t("tacho_status")}>
                {tachoStatusFields.map((f) => getLabeledValue(f.label, f.content))}
            </LabeledSection>
            {device && (
                <DeviceRemoveFromOrganizationDialog
                    devices={[convertDeviceToDeviceFlat(device)]}
                    open={removeDeviceToOrgModal.isOpen}
                    onClose={removeDeviceToOrgModal.toggleIsOpen}
                />
            )}
        </>
    );
};

export default DeviceGeneralTab;
